import { Component, inject, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ErrorToastService, ErrorToast } from './error-toast.service';

@Component({
  selector: 'app-error-toast',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      class="fixed top-4 right-4 z-[9999] flex flex-col gap-3 pointer-events-none"
      aria-live="polite"
      aria-atomic="true"
    >
      @for (toast of toasts(); track toast.id) {
        <div
          class="pointer-events-auto max-w-md w-full bg-elevated rounded-lg shadow-lg overflow-hidden border"
          animate.enter="toast-enter"
          animate.leave="toast-leave"
          [class.border-red-500]="toast.type === 'error'"
          [class.border-emerald-500]="toast.type === 'success'"
          [class.border-amber-500]="toast.type === 'warning'"
          [class.border-blue-400]="toast.type === 'info'"
          role="alert"
        >
          <div class="flex items-start gap-3 p-4">
            <!-- Icon -->
            <div class="flex-shrink-0 mt-0.5">
              @switch (toast.type) {
                @case ('error') {
                  <svg class="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round"
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                }
                @case ('success') {
                  <svg class="w-5 h-5 text-emerald-500 u-hero-pop" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                }
                @case ('warning') {
                  <svg class="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round"
                      d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                }
                @case ('info') {
                  <svg class="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                }
              }
            </div>

            <!-- Content -->
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-text-primary">{{ toast.message }}</p>
              @if (toast.details) {
                <!-- Never truncated (poc1 item 5): a conflict's full text must be readable here or
                     reachable via the action link — an ellipsis hid the very workspace name the
                     user needed. Wraps instead. -->
                <p class="toast-details mt-1 text-xs text-text-muted whitespace-pre-line break-words">
                  {{ toast.details }}
                </p>
              }
              @if (toast.code) {
                <span class="inline-block mt-2 px-2 py-0.5 text-xs font-mono bg-surface rounded text-text-secondary">
                  {{ toast.code }}
                </span>
              }
              @if (toast.action) {
                <button
                  type="button"
                  class="mt-2 text-xs font-medium underline underline-offset-2 transition-opacity hover:opacity-70"
                  [class.text-emerald-500]="toast.type === 'success'"
                  [class.text-blue-400]="toast.type === 'info'"
                  [class.text-amber-500]="toast.type === 'warning'"
                  [class.text-red-500]="toast.type === 'error'"
                  (click)="handleAction(toast)"
                >
                  {{ toast.action.label }}
                </button>
              }
            </div>

            <!-- Dismiss Button -->
            <button
              type="button"
              class="flex-shrink-0 p-1 rounded hover:bg-hover transition-colors text-text-muted hover:text-text-primary"
              (click)="dismiss(toast.id)"
              aria-label="Dismiss"
            >
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Progress bar for auto-dismiss -->
          @if (toast.autoDismiss) {
            <div class="h-0.5">
              <div
                class="h-full animate-shrink-width"
                [class.bg-red-500]="toast.type === 'error'"
                [class.bg-emerald-500]="toast.type === 'success'"
                [class.bg-amber-500]="toast.type === 'warning'"
                [class.bg-blue-400]="toast.type === 'info'"
                [style.animation-duration]="toast.duration + 'ms'"
              ></div>
            </div>
          }
        </div>
      }
    </div>
  `,
  styles: [`
    @keyframes toast-enter-kf {
      from { opacity: 0; transform: translateX(110%); }
      to   { opacity: 1; transform: translateX(0); }
    }

    @keyframes toast-leave-kf {
      from { opacity: 1; transform: translateX(0); }
      to   { opacity: 0; transform: translateX(110%); }
    }

    @keyframes shrink-width {
      from { width: 100%; }
      to   { width: 0%; }
    }

    /* Applied by Angular animate.enter / animate.leave on each toast.
       Spring overshoot on enter; quick accelerate-out on leave. */
    .toast-enter {
      animation: toast-enter-kf var(--motion-base, 240ms) var(--ease-spring, cubic-bezier(0.34, 1.56, 0.64, 1));
    }

    .toast-leave {
      animation: toast-leave-kf var(--motion-fast, 150ms) var(--ease-accelerate, cubic-bezier(0.4, 0, 1, 1)) forwards;
    }

    .animate-shrink-width {
      animation: shrink-width linear forwards;
    }

    @media (prefers-reduced-motion: reduce) {
      .toast-enter, .toast-leave { animation-duration: 0.01ms; }
    }
  `],
})
export class ErrorToastComponent {
  private readonly errorToastService = inject(ErrorToastService);

  readonly toasts = computed(() => this.errorToastService.toasts());

  dismiss(id: string): void {
    this.errorToastService.dismiss(id);
  }

  handleAction(toast: ErrorToast): void {
    toast.action?.onClick();
    this.errorToastService.dismiss(toast.id);
  }
}
